import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import TermsOfService from './pages/TermsOfService';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import Units from './pages/Units';
import Leases from './pages/Leases';
import Transactions from './pages/Transactions';
import Maintenance from './pages/Maintenance';
import Invoices from './pages/Invoices';
import ManagementInvoices from './pages/ManagementInvoices';
import Employees from './pages/Employees';
import Payroll from './pages/Payroll';
import TimeEntries from './pages/TimeEntries';
import BankAccounts from './pages/BankAccounts';
import Reconciliation from './pages/Reconciliation';
import ChequeManagement from './pages/ChequeManagement';
import OnlinePayments from './pages/OnlinePayments';
import MeetingMinutes from './pages/MeetingMinutes';
import BylawsPolicies from './pages/BylawsPolicies';
import AGMDocuments from './pages/AGMDocuments';
import Suppliers from './pages/Suppliers';
import Communications from './pages/Communications';
import Collections from './pages/Collections';
import RentCollections from './pages/RentCollections';
import MaintenanceFeeCollections from './pages/MaintenanceFeeCollections';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import AuditLog from './pages/AuditLog';
import Import from './pages/Import';
import Settings from './pages/Settings';
import './App.css';

/*
 * Altam™ Operating Services
 * Property, Finance, and Governance — Unified
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

function PrivateRoute({ children, allowedRoles = [] }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/properties" element={<PrivateRoute><Properties /></PrivateRoute>} />
      <Route path="/properties/:id" element={<PrivateRoute><PropertyDetail /></PrivateRoute>} />
      <Route path="/units" element={<PrivateRoute><Units /></PrivateRoute>} />
      <Route path="/leases" element={<PrivateRoute><Leases /></PrivateRoute>} />
      <Route path="/transactions" element={<PrivateRoute><Transactions /></PrivateRoute>} />
      <Route path="/maintenance" element={<PrivateRoute><Maintenance /></PrivateRoute>} />
      <Route path="/invoices" element={<PrivateRoute><Invoices /></PrivateRoute>} />
      <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
      <Route path="/payroll" element={<PrivateRoute allowedRoles={['admin', 'manager']}><Payroll /></PrivateRoute>} />
      <Route path="/time-entries" element={<PrivateRoute><TimeEntries /></PrivateRoute>} />
      <Route path="/bank-accounts" element={<PrivateRoute><BankAccounts /></PrivateRoute>} />
      <Route path="/reconciliation" element={<PrivateRoute allowedRoles={['admin', 'manager']}><Reconciliation /></PrivateRoute>} />
      <Route path="/cheques" element={<PrivateRoute allowedRoles={['admin', 'manager']}><ChequeManagement /></PrivateRoute>} />
      <Route path="/online-payments" element={<PrivateRoute allowedRoles={['admin', 'manager']}><OnlinePayments /></PrivateRoute>} />
      <Route path="/meeting-minutes" element={<PrivateRoute allowedRoles={['admin', 'manager', 'board']}><MeetingMinutes /></PrivateRoute>} />
      <Route path="/bylaws" element={<PrivateRoute allowedRoles={['admin', 'manager', 'board', 'resident']}><BylawsPolicies /></PrivateRoute>} />
      <Route path="/agm-documents" element={<PrivateRoute allowedRoles={['admin', 'manager', 'board', 'resident']}><AGMDocuments /></PrivateRoute>} />
      <Route path="/suppliers" element={<PrivateRoute><Suppliers /></PrivateRoute>} />
      <Route path="/communications" element={<PrivateRoute><Communications /></PrivateRoute>} />
      <Route path="/collections" element={<PrivateRoute><Collections /></PrivateRoute>} />
      <Route path="/collections/rent" element={<PrivateRoute><RentCollections /></PrivateRoute>} />
      <Route path="/collections/maintenance" element={<PrivateRoute><MaintenanceFeeCollections /></PrivateRoute>} />
      <Route path="/invoices/management" element={<PrivateRoute><ManagementInvoices /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="/users" element={<PrivateRoute allowedRoles={['admin']}><UserManagement /></PrivateRoute>} />
      <Route path="/audit-log" element={<PrivateRoute allowedRoles={['admin']}><AuditLog /></PrivateRoute>} />
      <Route path="/import" element={<PrivateRoute allowedRoles={['admin', 'manager']}><Import /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
